'use client';

import * as React from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { cn } from '@/lib/utils';
import {
  MODAL_SIZES,
  MODAL_HEIGHTS,
  HEADER_CLASSES,
  FOOTER_CLASSES,
  SPACING,
  ANIMATION_DURATION,
  type ModalSize,
  type ModalHeight,
} from '@/components/shared/modal-sheet-constants';
import { cleanupPointerEvents } from '@/components/shared/utils/modalCleanup';

/**
 * AlertModal Props Interface
 *
 * Similar to BaseModal but uses AlertDialog for semantic alert dialogs.
 * Based on audiences best practices - they use AlertDialog for all modals.
 *
 * Use AlertModal for:
 * - Confirmation dialogs
 * - Important notifications
 * - User decisions
 * - Any modal that requires user attention
 */
interface AlertModalProps {
  /**
   * Whether the modal is open
   */
  open: boolean;

  /**
   * Callback when open state changes
   */
  onOpenChange: (open: boolean) => void;

  /**
   * Modal title
   */
  title?: React.ReactNode;

  /**
   * Modal description
   */
  description?: string;

  /**
   * Modal size (width)
   * @default 'lg'
   */
  size?: ModalSize;

  /**
   * Modal height constraint
   * @default 'auto'
   */
  height?: ModalHeight;

  /**
   * Modal content
   */
  children?: React.ReactNode;

  /**
   * Optional footer content (buttons, actions)
   */
  footer?: React.ReactNode;

  /**
   * Whether to show the close button in top-right
   * @default true
   */
  showCloseButton?: boolean;

  /**
   * Additional className for content container
   */
  className?: string;

  /**
   * Callback fired when modal is closed (before onOpenChange)
   */
  onClose?: () => void;

  /**
   * Prevent closing modal by clicking overlay
   * @default false
   */
  disableOverlayClose?: boolean;

  /**
   * Prevent closing modal with ESC key
   * @default false
   */
  disableEscapeClose?: boolean;

  /**
   * Prevent closing when nested modal/sheet is open
   */
  nestedModalOpen?: boolean | React.RefObject<boolean>;

  /**
   * Callback for interact outside events
   */
  onInteractOutside?: (event: Event) => void;

  /**
   * Disable overflow on content wrapper for custom scroll layouts
   * Use this when content has its own scrollable panels (e.g., 2-panel layouts)
   * @default false
   */
  disableContentOverflow?: boolean;
}

/**
 * AlertModal - Alert dialog variant using AlertDialog
 *
 * Based on audiences best practices. Uses AlertDialog for semantic meaning
 * and proper accessibility attributes (role="alertdialog").
 *
 * @example Basic usage
 * ```tsx
 * const [open, setOpen] = useState(false);
 *
 * <AlertModal
 *   open={open}
 *   onOpenChange={setOpen}
 *   title="Confirm Action"
 *   description="Are you sure you want to continue?"
 *   size="md"
 *   footer={
 *     <>
 *       <CancelButton onClick={() => setOpen(false)}>Cancel</CancelButton>
 *       <CTAButton onClick={handleConfirm}>Confirm</CTAButton>
 *     </>
 *   }
 * >
 *   <p>This action cannot be undone.</p>
 * </AlertModal>
 * ```
 */
export const AlertModal = React.forwardRef<HTMLDivElement, AlertModalProps>(
  (
    {
      open,
      onOpenChange,
      title,
      description,
      size = 'lg',
      height = 'auto',
      children,
      footer,
      showCloseButton = true,
      className,
      onClose,
      disableOverlayClose = false,
      disableEscapeClose = false,
      nestedModalOpen = false,
      onInteractOutside,
      disableContentOverflow = false,
    },
    ref
  ) => {
    // Production-safe cleanup: Remove stuck pointer-events when modal closes
    React.useEffect(() => {
      if (!open) {
        const timeoutId = setTimeout(() => {
          cleanupPointerEvents('AlertModal', false);
        }, ANIMATION_DURATION.modal);
        return () => clearTimeout(timeoutId);
      }
    }, [open]);

    // Check if nested modal is open
    const isNestedModalOpen = React.useMemo(() => {
      if (typeof nestedModalOpen === 'boolean') {
        return nestedModalOpen;
      }
      if (nestedModalOpen && 'current' in nestedModalOpen) {
        return nestedModalOpen.current;
      }
      return false;
    }, [nestedModalOpen]);

    // Handle dialog state change
    const handleOpenChange = React.useCallback(
      (newOpen: boolean) => {
        // Don't close if nested modal is open
        if (!newOpen && isNestedModalOpen) {
          return;
        }

        if (!newOpen && onClose) {
          onClose();
        }
        onOpenChange(newOpen);
      },
      [onClose, onOpenChange, isNestedModalOpen]
    );

    return (
      <AlertDialog open={open} onOpenChange={handleOpenChange}>
        <AlertDialogContent
          ref={ref}
          className={cn(
            MODAL_SIZES[size],
            MODAL_HEIGHTS[height],
            'flex flex-col',
            className
          )}
          showCloseButton={showCloseButton}
          onPointerDownOutside={(e) => {
            if (disableOverlayClose || isNestedModalOpen) {
              e.preventDefault();
              return;
            }
            handleOpenChange(false);
          }}
          onEscapeKeyDown={(e) => {
            if (disableEscapeClose || isNestedModalOpen) {
              e.preventDefault();
            }
          }}
        >
          {/* Header - Always include AlertDialogTitle for accessibility */}
          <AlertDialogHeader className={HEADER_CLASSES.modal}>
            {title ? (
              <AlertDialogTitle>{title}</AlertDialogTitle>
            ) : (
              <VisuallyHidden asChild>
                <AlertDialogTitle>Alert</AlertDialogTitle>
              </VisuallyHidden>
            )}
            {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
          </AlertDialogHeader>

          {/* Content */}
          {children && (
            <div className={cn(
              'flex-1 min-h-0 py-4',
              disableContentOverflow ? 'flex flex-col overflow-hidden' : 'overflow-y-auto',
              !title && !description && SPACING.modalPadding
            )}>
              {children}
            </div>
          )}

          {/* Footer */}
          {footer && (
            <AlertDialogFooter className={FOOTER_CLASSES.modal}>
              {footer}
            </AlertDialogFooter>
          )}
        </AlertDialogContent>
      </AlertDialog>
    );
  }
);

AlertModal.displayName = 'AlertModal';

